// KML and GPX export of reach areas and routes.

import { cellCenter, type GridSpec, type LngLat } from './geo';
import { AGL_STOPS, ramp } from './colors';

const esc = (s: string) => s.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);

export interface Route3D {
  name: string;
  /** [lng, lat, alt MSL] */
  points: [number, number, number][];
}

function kmlColor(c: [number, number, number], alpha: number): string {
  const h = (n: number) => n.toString(16).padStart(2, '0');
  return h(alpha) + h(c[2]) + h(c[1]) + h(c[0]); // aabbggrr
}

function routePlacemark(r: Route3D): string {
  const coords = r.points.map((p) => `${p[0].toFixed(6)},${p[1].toFixed(6)},${p[2].toFixed(0)}`).join(' ');
  return `<Placemark><name>${esc(r.name)}</name><styleUrl>#route</styleUrl><LineString><altitudeMode>absolute</altitudeMode><coordinates>${coords}</coordinates></LineString></Placemark>`;
}

/** Bands of height above ground used to group cells in the KML export. */
export const KML_BANDS = [0, 150, 400, 800, 1500, 2500];

/**
 * Reach area as KML. Cells are grouped into height-above-ground bands; consecutive cells in a row
 * that share a band are merged into one rectangle to keep the file small.
 */
export function reachToKml(spec: GridSpec, alt: Float32Array, elev: Float32Array, title: string, route?: Route3D): string {
  const bands = KML_BANDS.map(() => [] as string[]);
  const band = (agl: number) => {
    let b = 0;
    for (let i = 0; i < KML_BANDS.length; i++) if (agl >= KML_BANDS[i]) b = i;
    return b;
  };
  for (let r = 0; r < spec.ny; r++) {
    let c = 0;
    while (c < spec.nx) {
      const i = r * spec.nx + c;
      if (Number.isNaN(alt[i])) {
        c++;
        continue;
      }
      const b = band(alt[i] - elev[i]);
      let e = c;
      while (e + 1 < spec.nx && !Number.isNaN(alt[i + e + 1 - c]) && band(alt[i + e + 1 - c] - elev[i + e + 1 - c]) === b) e++;
      const [w, n] = cellCenter(spec, c - 0.5, r - 0.5);
      const [ea, s] = cellCenter(spec, e + 0.5, r + 0.5);
      const ring = [
        [w, n],
        [ea, n],
        [ea, s],
        [w, s],
        [w, n],
      ]
        .map((p) => `${p[0].toFixed(6)},${p[1].toFixed(6)}`)
        .join(' ');
      bands[b].push(`<Polygon><outerBoundaryIs><LinearRing><coordinates>${ring}</coordinates></LinearRing></outerBoundaryIs></Polygon>`);
      c = e + 1;
    }
  }
  const styles = KML_BANDS.map(
    (v, i) =>
      `<Style id="b${i}"><LineStyle><width>0</width></LineStyle><PolyStyle><color>${kmlColor(ramp(AGL_STOPS, v), 0x99)}</color><outline>0</outline></PolyStyle></Style>`,
  ).join('');
  const placemarks = bands
    .map((polys, i) => {
      if (!polys.length) return '';
      const label = i + 1 < KML_BANDS.length ? `${KML_BANDS[i]}-${KML_BANDS[i + 1]} m AGL` : `> ${KML_BANDS[i]} m AGL`;
      return `<Placemark><name>${label}</name><styleUrl>#b${i}</styleUrl><MultiGeometry>${polys.join('')}</MultiGeometry></Placemark>`;
    })
    .join('');
  return kmlDoc(title, styles + placemarks + (route ? routePlacemark(route) : ''));
}

export function routeToKml(r: Route3D): string {
  return kmlDoc(r.name, routePlacemark(r));
}

function kmlDoc(name: string, body: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${esc(name)}</name><Style id="route"><LineStyle><color>ff0000ff</color><width>3</width></LineStyle></Style>${body}</Document></kml>
`;
}

export function routeToGpx(r: Route3D): string {
  const pts = r.points
    .map((p) => `<rtept lat="${p[1].toFixed(6)}" lon="${p[0].toFixed(6)}"><ele>${p[2].toFixed(0)}</ele></rtept>`)
    .join('');
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Glide Planner" xmlns="http://www.topografix.com/GPX/1/1"><rte><name>${esc(r.name)}</name>${pts}</rte></gpx>
`;
}

export function download(filename: string, text: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export type { LngLat };
